// RigSentinel — Daily Operations Module Foundation (Phase RCE-2)
//
// KIND REGISTRY
// -------------
// Every document in the Daily Operations family registers as a `kind` here.
// Only DDR is enabled in RCE-2; the other five reserve their architectural
// slot so future phases plug in without breaking the contract.
//
// Module Ownership Declaration (per RCE-2 architectural principle):
//   * Owns      — the information the kind is the authoritative source for
//   * Consumes  — info pulled in from the RCE Context Engine (resolver)
//   * Publishes — info this kind makes available to other modules
//   * Overrides — fields the document is permitted to override on a
//                 document-scope basis (NEVER on master scope)
//
// All five "future" kinds are declared `enabled: false`. The Daily Ops
// editor scaffold refuses to navigate to a disabled kind.

export const CONTEXT_VERSION = "RCE-1.0";

/** Ownership descriptor for a Daily Operations kind. */
function ownership({ owns = [], consumes = [], publishes = [], overrides = [] } = {}) {
  return { owns, consumes, publishes, overrides };
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const DAILY_OPS_KINDS = Object.freeze({
  ddr: {
    id: "ddr",
    label: "Daily Drilling Report",
    enabled: true,
    storageKeySuffix: "ddr",
    ownership: ownership({
      owns: [
        "dailyOps.reportDate", "dailyOps.reportNumber",
        "dailyOps.tour", "dailyOps.shift",
        "dailyOps.preparedBy", "dailyOps.position",
        "dailyOps.activities",
        "dailyOps.accomplishments", "dailyOps.problems",
        "dailyOps.correctiveActions", "dailyOps.currentStatus",
        "dailyOps.nextPlan",
      ],
      consumes: [
        "project.operator", "project.client", "project.country", "project.field",
        "rig.name", "rig.contractor",
        "well.name", "well.environment", "well.waterDepthFt",
        "well.holeSection", "well.holeSizeIn",
        "well.currentMdFt", "well.currentTvdFt",
        "well.casingShoeMdFt", "well.casingShoeTvdFt",
        "bha.id", "bha.revision",
        "drillString.id", "drillString.revision",
        "mud.weightInPpg",
        "operation.current",
      ],
      publishes: [
        // Future readers (timeline, AI report polisher) can subscribe to
        // these. RCE-2 only registers the slots — no live publisher yet.
        "dailyOps.lastReportDate",
        "dailyOps.totalFootageDrilled",
        "dailyOps.totalNptHours",
      ],
      overrides: [
        // Fields the DDR is permitted to override on a document-scope basis.
        // Master-scope overrides are NEVER created from a DDR — that's the
        // job of the underlying module's editor.
        "well.holeSection", "well.holeSizeIn",
        "well.currentMdFt", "well.currentTvdFt",
        "well.casingShoeMdFt",
        "mud.weightInPpg",
        "operation.current",
      ],
    }),
  },
  shiftReport: {
    id: "shiftReport",
    label: "Shift Report",
    enabled: false,
    storageKeySuffix: "shift",
    ownership: ownership({
      owns: ["dailyOps.shiftSummary"],
      consumes: ["project.operator", "well.name", "operation.current"],
      publishes: [],
      overrides: [],
    }),
  },
  tourReport: {
    id: "tourReport",
    label: "Tour Report",
    enabled: false,
    storageKeySuffix: "tour",
    ownership: ownership({
      owns: ["dailyOps.tourSummary"],
      consumes: ["well.name", "operation.current"],
      publishes: [],
      overrides: [],
    }),
  },
  dailyOpsLog: {
    id: "dailyOpsLog",
    label: "Daily Operations Log",
    enabled: false,
    storageKeySuffix: "log",
    ownership: ownership({
      owns: ["dailyOps.logEntries"],
      consumes: ["well.name", "operation.current"],
      publishes: [],
      overrides: [],
    }),
  },
  supervisorNotes: {
    id: "supervisorNotes",
    label: "Supervisor Notes",
    enabled: false,
    storageKeySuffix: "supnotes",
    ownership: ownership({
      owns: ["dailyOps.notes"],
      consumes: ["well.name"],
      publishes: [],
      overrides: [],
    }),
  },
  endOfDayReport: {
    id: "endOfDayReport",
    label: "End-of-Day Report",
    enabled: false,
    storageKeySuffix: "eod",
    ownership: ownership({
      owns: ["dailyOps.dayEndSummary"],
      consumes: ["project.operator", "well.name", "well.currentMdFt"],
      publishes: [],
      overrides: [],
    }),
  },
});

export const DAILY_OPS_KIND_IDS = Object.freeze(Object.keys(DAILY_OPS_KINDS));

export function isKnownKind(kind) {
  return !!DAILY_OPS_KINDS[kind];
}

export function isKindEnabled(kind) {
  return !!DAILY_OPS_KINDS[kind]?.enabled;
}

export function getKind(kind) {
  return DAILY_OPS_KINDS[kind] || null;
}

export function listEnabledKinds() {
  return DAILY_OPS_KIND_IDS.filter((k) => DAILY_OPS_KINDS[k].enabled);
}
